import React, { FC } from 'react'
import { ModuleNames } from '../../../constants/componentNames.constants'
import { ProductPanel } from '../../global/leftNav/style'

//* components
import { HERO_USECASES } from '../../../constants/useCases.constants'
import CardSlider from '../../global/cardSlider'
import ContentBlock from '../../global/contentBlock'
import ContentColumns from '../../global/contentColumns'
import ContentRows from '../../global/contentRows'
import Divider from '../../global/divider'
import { FeaturedAsset } from '../../global/featuredAsset/featuredAsset'
import FeaturedResource from '../../global/featuredResource'
import Grid from '../../global/grid'
import Hero from '../../global/hero'
import Icons from '../../global/icons'
import Image from '../../global/image'
import KeyStats from '../../global/keyStats'
import LeftNav from '../../global/leftNav'
import MainCTA from '../../global/mainCta'
import MediaText from '../../global/mediaText'
import MediaTextVariants from '../../global/mediaTextVariants'
import ParagraphBlock from '../../global/paragraphBlock'
import PlatformDiagram from '../../global/platformDiagram'
import Quote from '../../global/quote'
import RawHTML from '../../global/rawHTML'
import ResourceLists from '../../global/resourceLists'
import ResourceRow from '../../global/resourceRow'
import ResourceTabs from '../../global/resourceTabs'
import SEOTags from '../../global/seoTags'
import Slider from '../../global/slider'
import Table from '../../global/table'
import Tiles from '../../global/tiles'
import VideoPlayer from '../../global/videoPlayer'
import VideoSpotlight from '../../global/videoSpotlight'

import CommonStickyNav from '../../global/commonStickyNav'

type Props = {
  data: any
  availableLocales: any
  locale: string
  slug: string
  pageType: string
  location: any
  path: string
}

export const Modules: FC<Props> = ({
  data,
  availableLocales,
  locale,
  slug,
  pageType,
  location,
  path,
}) => {
  const productPageContent = data?.flexSection || data?.modules || []
  const siteSection = pageType === 'flex' ? 'Flex' : 'Product'
  let pageTitle: string = ''

  if (pageType === 'product' || pageType === 'flex') {
    pageTitle = data?.name || data?.entryName
  } else {
    pageTitle = data?.pageName || data?.name
  }

  const useCase: string = data?.hero?.useCase
  const dynamicPageContent = productPageContent.map((module: any, i: number) => {
    switch (module.__typename) {
      case ModuleNames.DIVIDER:
        return <Divider data={module} key={`page-component--${i}`} />

      case ModuleNames.MEDIA_TEXT:
        return <MediaText data={module} pageType={pageType} key={`page-component--${i}`} />

      case ModuleNames.MEDIA_ASSET:
        return (
          <MediaText
            data={module}
            hasMediaAsset={true}
            pageType={pageType}
            key={`page-component--${i}`}
          />
        )

      case ModuleNames.MEDIA_TEXT_VARIANTS:
        return <MediaTextVariants data={module} pageType={pageType} key={`page-component--${i}`} />

      case ModuleNames.CONTENT_ROWS:
        return <ContentRows data={module} key={`page-component--${i}`} />

      case ModuleNames.GLOBAL_KEY_STATS:
        return <KeyStats data={module} key={`page-component--${i}`} />

      case ModuleNames.VIDEO_SPOTLIGHT:
        return <VideoSpotlight data={module} key={`page-component--${i}`} />

      case ModuleNames.GLOBAL_CONTENT_COLUMNS:
        return <ContentColumns data={module} pageType={pageType} key={`page-component--${i}`} />

      case ModuleNames.GLOBAL_CONTENT_BLOCK:
        return <ContentBlock data={module} key={`page-component--${i}`} />

      case ModuleNames.RESOURCE_ROW:
        return (
          <ResourceRow
            data={module}
            pageType={pageType}
            key={`page-component--${i}`}
            locale={locale}
          />
        )

      case ModuleNames.ICONS:
        return <Icons data={module} key={`page-component--${i}`} />

      case ModuleNames.GRID:
        return <Grid data={module} key={`page-component--${i}`} />

      case ModuleNames.QUOTE:
        return <Quote data={module} key={`page-component--${i}`} />

      case ModuleNames.RAW_HTML:
        return <RawHTML data={module} key={`page-component--${i}`} />

      case ModuleNames.FEATURED_RESOURCE:
        return (
          <FeaturedResource
            data={module}
            pageLocale={locale}
            key={`page-component--${i}`}
            pageType={pageType}
          />
        )

      case ModuleNames.SLIDER:
        return <Slider data={module} key={`page-component--${i}`} />

      case ModuleNames.TABLE:
        return <Table data={module} key={`page-component--${i}`} />

      case ModuleNames.HERO:
        return (
          <Hero
            data={module}
            location={location}
            pageTitle={pageTitle}
            showBreadcrumbs={true}
            key={`page-component--${i}`}
            pageType={pageType}
            metaInformation={data.metaInformation}
            pageUseCase={data.pageUseCase}
            locale={locale}
            breadcrumb={data.breadcrumb}
          />
        )

      case ModuleNames.GLOBAL_MAIN_CTA:
        return <MainCTA data={module} locale={locale} key={`page-component--${i}`} />

      case ModuleNames.VIDEO_SPOTLIGHT:
        return <VideoSpotlight data={module} key={`page-component--${i}`} />

      case ModuleNames.RESOURCE_TABS:
        return <ResourceTabs data={module} key={`page-component--${i}`} />

      case ModuleNames.PLATFORM_DIAGRAM:
        return <PlatformDiagram data={module} key={`page-component--${i}`} />

      case ModuleNames.TILES:
        return <Tiles data={module} key={`page-component--${i}`} />

      case ModuleNames.WRAPPER_ASSET:
        const imageData = {
          imageFile: {
            file: {
              title: module.altText || '',
              url: module.image?.file?.url || '',
            },
          },
        }

        return <Image data={imageData} key={`page-component--${i}`} outerPage={pageType} />

      case ModuleNames.WRAPPER_VIDEO:
        const videoData = {
          image: {
            url: module.image?.file?.url || '/',
            altText: module.altText || '',
          },
          video: {
            start: module.start || 0,
            id: '',
            url: '',
            type: '',
            imageOverlay: module?.imageOverlay || '',
          },
        }

        if (module.youtubeId) {
          videoData.video.id = module.youtubeId
          videoData.video.type = 'youtube'
        }

        if (module.vimeoId) {
          videoData.video.id = module.vimeoId
          videoData.video.type = 'vimeo'
        }

        if (module.videoUrl) {
          videoData.video.url = module.videoUrl
          videoData.video.type = 'html-video'
        }

        if (module.tencentVideoUrl) {
          videoData.video.url = module.tencentVideoUrl
          videoData.video.type = 'tencent-video'
        }

        if (videoData.video.id || videoData.video.url) {
          return (
            <VideoPlayer
              videoId={videoData.video.id}
              videoUrl={videoData.video.url}
              width={'100%'}
              height={'429'}
              img={videoData.image.url}
              type={videoData.video.type}
              start={videoData.video.start}
              imageOverlay={videoData.video.imageOverlay}
              backgroundAltText={videoData.image.url}
              key={`page-component--${i}`}
              outerPage={pageType}></VideoPlayer>
          )
        } else {
          return null
        }

      case ModuleNames.PARAGRAPH_BLOCK:
        return <ParagraphBlock data={module} key={`page-component--${i}`} />

      case ModuleNames.ANCHOR_LINK:
        return <span key={`page-component--${i}`} id={module.identifier || ''}></span>

      case ModuleNames.RESOURCE_LISTS:
        return <ResourceLists data={module} key={`page-component--${i}`} />

      case ModuleNames.CARD_SLIDER:
        return <CardSlider data={module} key={`page-component--${i}`} />
    }
  })

  return (
    <div data-cy={`ProductPage--${pageType}`}>
      <SEOTags
        seoMetadata={data?.seoMetadata}
        slug={slug}
        path={path}
        locale={locale}
        siteSection={siteSection}
        availableLocales={availableLocales}
      />

      {data.hero && (
        <Hero
          data={data.hero}
          location={location}
          pageTitle={pageTitle}
          showBreadcrumbs={true}
          pageType={pageType}
          locale={locale}
          breadcrumb={
            useCase === HERO_USECASES.FULL_WIDTH_HERO
              ? data?.hero?.breadcrumb || data?.breadcrumb
              : data?.breadcrumb
          }
        />
      )}

      {useCase === HERO_USECASES.FULL_WIDTH_HERO && data?.featuredAsset && (
        <FeaturedAsset data={data?.featuredAsset} />
      )}

      {data.spotlight && <VideoSpotlight data={data.spotlight} />}

      {useCase === 'Full width hero' && pageType === 'aboutRPA' ? (
        <>
          <ProductPanel>
            <div className="leftPanel">
              <LeftNav data={data.pageNavigation} />
            </div>
            <div className="rightPanel">{dynamicPageContent}</div>
          </ProductPanel>
        </>
      ) : (
        <div>
          {data.pageNavigation && (
            <CommonStickyNav data={data.pageNavigation} stickyNavCta={data.stickyNavCta} />
          )}

          {dynamicPageContent}
        </div>
      )}

      {data.bottomCta && <MainCTA data={data.bottomCta} />}
    </div>
  )
}
